import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper, Divider, Link, Stack } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ExploreIcon from '@mui/icons-material/Explore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MessageIcon from '@mui/icons-material/Message';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: { xs: 60, sm: 80 },
              color: 'primary.main',
              mb: 2,
            }}
          />
          
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '4rem', sm: '6rem' },
              fontWeight: 800,
              color: 'primary.main',
              lineHeight: 1,
              mb: 1,
            }}
          >
            404
          </Typography>
          
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#1a1a1a',
              mb: 2,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            Page Not Found
          </Typography>
          
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            The page you're looking for doesn't exist or has been moved. If you
            got here from a link on the site, something's off — please let us
            know.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              mb: 3,
            }}
          >
            <Button
              variant="contained"
              startIcon={<ExploreIcon />}
              onClick={() => navigate('/explore')}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Browse Guest Spots
            </Button>

            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'rgba(45, 32, 112, 0.04)',
                },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Go Home
            </Button>
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Or jump to
            </Typography>
          </Divider>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Button
              size="small"
              startIcon={<DashboardIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Dashboard
            </Button>
            <Button
              size="small"
              startIcon={<MessageIcon />}
              onClick={() => navigate('/messages')}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Messages
            </Button>
            <Link
              href="mailto:will@guest.stream?subject=Broken%20link%20on%20guest.stream"
              underline="hover"
              sx={{ fontSize: '0.875rem', color: 'text.secondary', px: 1 }}
            >
              Report broken link
            </Link>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default NotFoundPage;
